import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-bold tracking-[.02em] transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-on-accent hover:bg-accent-hover',
        secondary: 'bg-surface-2 text-ink hover:bg-border',
        outline: 'border border-border bg-transparent text-ink hover:bg-ink hover:text-surface hover:border-ink',
        ghost: 'bg-transparent text-ink hover:bg-surface-2',
        danger: 'bg-danger text-white hover:opacity-90',
        link: 'bg-transparent text-accent-ink underline-offset-4 hover:underline p-0 h-auto font-semibold tracking-normal',
      },
      // max-md bumps the tap area to the 44px iOS minimum on phones only, so
      // the dense desktop button sizing is untouched. min-h/min-w avoid
      // conflicting with the fixed h-/w- height so the larger value wins.
      size: {
        default: 'h-10 px-5 max-md:min-h-11',
        sm: 'h-8 px-3.5 text-[13px] max-md:min-h-11',
        lg: 'h-12 px-7 text-base',
        icon: 'h-9 w-9 max-md:min-h-11 max-md:min-w-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => {
    return (
      <button className={cn(buttonVariants({ variant, size }), className)} ref={ref} disabled={disabled || loading} {...props}>
        {loading ? (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
